import { createTheme, inputLabelClasses, tableCellClasses } from '@mui/material';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import CheckBoxOutlineBlankOutlinedIcon from '@mui/icons-material/CheckBoxOutlineBlankOutlined';
import IndeterminateCheckBoxOutlinedIcon from '@mui/icons-material/IndeterminateCheckBoxOutlined';
import {
  FORM_FIELD_FONT_SIZE,
  FORM_FIELD_FONT_WEIGHT,
  FORM_FIELD_HEIGHT,
  FORM_FIELD_INPUT_PADDING,
} from '../form-field';

// Used only to create transitions
const muiTheme = createTheme();

export const createComponents = () => {
  return {
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontSize: 14,
          fontWeight: 600,
          letterSpacing: 0
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          textTransform: 'none'
        },
        sizeSmall: {
          padding: '6px 16px'
        },
        sizeMedium: {
          padding: '8px 20px'
        },
        sizeLarge: {
          padding: '11px 24px'
        },
        textSizeSmall: {
          padding: '7px 12px'
        },
        textSizeMedium: {
          padding: '9px 16px'
        },
        textSizeLarge: {
          padding: '12px 16px'
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20
        }
      }
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '32px 24px',
          '&:last-child': {
            paddingBottom: '32px'
          }
        }
      }
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: {
          variant: 'h6'
        },
        subheaderTypographyProps: {
          variant: 'body2'
        }
      },
      styleOverrides: {
        root: {
          padding: '32px 24px 16px'
        }
      }
    },
    MuiCheckbox: {
      defaultProps: {
        checkedIcon: <CheckBoxOutlinedIcon />,
        color: 'primary',
        icon: <CheckBoxOutlineBlankOutlinedIcon />,
        indeterminateIcon: <IndeterminateCheckBoxOutlinedIcon />
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500
        }
      }
    },
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          boxSizing: 'border-box'
        },
        html: {
          MozOsxFontSmoothing: 'grayscale',
          WebkitFontSmoothing: 'antialiased',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100%',
          width: '100%'
        },
        body: {
          display: 'flex',
          flex: '1 1 auto',
          flexDirection: 'column',
          minHeight: '100%',
          width: '100%'
        },
        '#__next': {
          display: 'flex',
          flex: '1 1 auto',
          flexDirection: 'column',
          height: '100%',
          width: '100%'
        },
        '#nprogress': {
          pointerEvents: 'none'
        },
        '#nprogress .bar': {
          height: 3,
          left: 0,
          position: 'fixed',
          top: 0,
          width: '100%',
          zIndex: 2000
        }
      }
    },
    MuiIconButton: {
      defaultProps: {
        color: 'inherit'
      },
      styleOverrides: {
        root: {
          color: 'inherit'
        },
        sizeSmall: {
          padding: 4
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          '&::placeholder': {
            opacity: 1
          }
        }
      }
    },
    MuiInput: {
      styleOverrides: {
        input: {
          fontSize: 14,
          fontWeight: 500,
          lineHeight: '24px'
        }
      }
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          borderRadius: 8,
          borderStyle: 'solid',
          borderWidth: 1,
          overflow: 'hidden',
          transition: muiTheme.transitions.create([
            'border-color',
            'box-shadow'
          ]),
          '&:before': {
            display: 'none'
          },
          '&:after': {
            display: 'none'
          }
        },
        input: {
          fontSize: 14,
          fontWeight: 500,
          lineHeight: '24px'
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&:not(.MuiInputBase-multiline)': { minHeight: FORM_FIELD_HEIGHT },
        },
        input: {
          fontSize: FORM_FIELD_FONT_SIZE,
          fontWeight: FORM_FIELD_FONT_WEIGHT,
          lineHeight: '24px',
          padding: FORM_FIELD_INPUT_PADDING,
        },
        notchedOutline: {
          transition: muiTheme.transitions.create([
            'border-color',
            'box-shadow'
          ])
        }
      }
    },
    // MUI X v8 pickers usam componentes próprios — não herdam MuiOutlinedInput
    MuiPickersTextField: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          margin: 0,
        },
      },
    },
    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: {
          alignItems: 'center',
          boxSizing: 'border-box',
          height: FORM_FIELD_HEIGHT,
          minHeight: FORM_FIELD_HEIGHT,
          maxHeight: FORM_FIELD_HEIGHT,
          paddingTop: 0,
          paddingBottom: 0,
          paddingLeft: 0,
          paddingRight: '8px',
          overflow: 'hidden',
          '&.Mui-disabled': {
            height: FORM_FIELD_HEIGHT,
            minHeight: FORM_FIELD_HEIGHT,
            maxHeight: FORM_FIELD_HEIGHT,
          },
          '& .MuiPickersSectionList-root': {
            flex: 1,
            minWidth: 0,
            alignSelf: 'center',
            padding: '0 12px',
            paddingRight: '4px',
            fontSize: FORM_FIELD_FONT_SIZE,
            fontWeight: FORM_FIELD_FONT_WEIGHT,
            lineHeight: '24px',
          },
          '& .MuiInputAdornment-root': {
            marginLeft: 0,
            marginRight: 0,
            flexShrink: 0,
            height: 'auto',
            maxHeight: 'none',
          },
          '& .MuiInputAdornment-root .MuiIconButton-root': {
            padding: 4,
          },
          '& .MuiInputAdornment-root .MuiSvgIcon-root': {
            fontSize: '1.125rem',
          },
        },
      },
    },
    MuiPickersInputBase: {
      styleOverrides: {
        root: {
          fontSize: FORM_FIELD_FONT_SIZE,
          height: FORM_FIELD_HEIGHT,
          minHeight: FORM_FIELD_HEIGHT,
          maxHeight: FORM_FIELD_HEIGHT,
          padding: 0,
          boxSizing: 'border-box',
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          fontWeight: 500,
          [`&.${inputLabelClasses.filled}`]: {
            transform: 'translate(12px, 18px) scale(1)'
          },
          [`&.${inputLabelClasses.shrink}`]: {
            [`&.${inputLabelClasses.standard}`]: {
              transform: 'translate(0, -1.5px) scale(0.85)'
            },
            [`&.${inputLabelClasses.filled}`]: {
              transform: 'translate(12px, 6px) scale(0.85)'
            },
            [`&.${inputLabelClasses.outlined}`]: {
              transform: 'translate(14px, -9px) scale(0.85)'
            }
          }
        }
      }
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          overflow: 'hidden'
        }
      }
    },
    MuiLink: {
      defaultProps: {
        underline: 'hover'
      }
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          marginRight: '16px',
          minWidth: 'unset'
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none'
        }
      }
    },
    MuiPopover: {
      defaultProps: {
        elevation: 16
      }
    },
    MuiRadio: {
      defaultProps: {
        color: 'primary'
      }
    },
    MuiSwitch: {
      defaultProps: {
        color: 'primary'
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: 14,
          fontWeight: 500,
          lineHeight: 1.71,
          minWidth: 'auto',
          paddingLeft: 0,
          paddingRight: 0,
          textTransform: 'none',
          '& + &': {
            marginLeft: 24
          }
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '15px 16px'
        }
      }
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          borderBottom: 'none',
          [`& .${tableCellClasses.root}`]: {
            borderBottom: 'none',
            fontSize: 14,
            fontWeight: 600,
            lineHeight: 1,
            letterSpacing: 0.5,
            textTransform: 'none'
          },
          [`& .${tableCellClasses.paddingCheckbox}`]: {
            paddingTop: 4,
            paddingBottom: 4
          }
        }
      }
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
      }
    },
    MuiTypography: {
      defaultProps: {
        color: 'text.primary'
      }
    }
  };
};
